import {
    OPERATIONS_REQUEST,
    OPERATIONS_SUCCESS,
    OPERATIONS_FAILURE,
    OPERATIONS_SESSION_UPDATED,
    OPERATIONS_SECRETARIES_SUCCESS,
    OPERATIONS_JUDGES_SUCCESS,
} from 'actions';

const initialState = {
    dataByCompetition: {},
    isFetched: [],
    isFetching: [],
    // Competitions whose fetch FAILED. Tracked separately from isFetched,
    // which must keep meaning "we have data": without this the hook sees
    // neither fetched nor fetching and dispatches again immediately, turning
    // one failed request into a storm of them against the API.
    failed: [],
    // Who each competition's mats may be handed to. Kept beside the floor
    // rather than in its own reducer because it is read only by the floor and
    // has exactly the same lifetime.
    secretariesByCompetition: {},
    // Whether THIS user may assign secretaries at all, per competition.
    // Distinct from the list being empty — see the action.
    canAssignSecretaryByCompetition: {},
    // The competition's judges a mat may be given (17 Oct 2026).
    judgesByCompetition: {},
};

/**
 * The tatami floor, one entry per competition.
 *
 * OPERATIONS_SESSION_UPDATED replaces a SINGLE session in place and leaves
 * every other object identical by reference. That is what lets the queue UI
 * re-render one card after an action instead of rebuilding the list — a fresh
 * array for every session would remount every row and throw away scroll
 * position and focus, which is precisely the result-entry problem this
 * project already fixed once.
 */
const listReducer = (state = initialState, action = {}) => {
    switch (action.type) {
        case OPERATIONS_REQUEST:
            return {
                ...state,
                isFetching: [...state.isFetching.filter(id => id !== action.competitionId), action.competitionId],
                failed: state.failed.filter(id => id !== action.competitionId),
            };

        case OPERATIONS_SECRETARIES_SUCCESS:
            return {
                ...state,
                secretariesByCompetition: {
                    ...state.secretariesByCompetition,
                    [action.competitionId]: action.data || [],
                },
                canAssignSecretaryByCompetition: {
                    ...state.canAssignSecretaryByCompetition,
                    [action.competitionId]: Boolean(action.allowed),
                },
            };

        case OPERATIONS_JUDGES_SUCCESS:
            return {
                ...state,
                judgesByCompetition: {
                    ...state.judgesByCompetition,
                    [action.competitionId]: action.data || [],
                },
            };

        case OPERATIONS_SUCCESS:
            return {
                ...state,
                dataByCompetition: {
                    ...state.dataByCompetition,
                    [action.competitionId]: action.data || [],
                },
                isFetched: [...state.isFetched.filter(id => id !== action.competitionId), action.competitionId],
                isFetching: state.isFetching.filter(id => id !== action.competitionId),
                failed: state.failed.filter(id => id !== action.competitionId),
            };

        case OPERATIONS_FAILURE:
            return {
                ...state,
                isFetching: state.isFetching.filter(id => id !== action.competitionId),
                failed: [...state.failed.filter(id => id !== action.competitionId), action.competitionId],
            };

        case OPERATIONS_SESSION_UPDATED: {
            if (!action.session || !action.session.id) {
                return state;
            }

            const dataByCompetition = {};
            let changed = false;

            Object.keys(state.dataByCompetition).forEach(competitionId => {
                const sessions = state.dataByCompetition[competitionId];
                const index = sessions.findIndex(s => s.id === action.session.id);

                if (index === -1) {
                    dataByCompetition[competitionId] = sessions;
                    return;
                }

                // Only the changed session becomes a new object; its siblings
                // keep their identity so React skips them.
                const next = sessions.slice();
                next[index] = action.session;
                dataByCompetition[competitionId] = next;
                changed = true;
            });

            return changed ? { ...state, dataByCompetition } : state;
        }

        default:
            return state;
    }
};

export default listReducer;
